// src/pages/karma.theme.ts

import { createTheme, darken, lighten } from '@mui/material/styles'

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    performance: true
    feeling: true
    security: true
    installation: true
    correlation: true
    office_hours: true
    folderStatus: true
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    white: string
    black: string
    correlation: Palette['primary']
    lightgray: Palette['primary']
  }

  interface PaletteOptions {
    white?: string
    black?: string
    correlation?: PaletteOptions['primary']
    lightgray?: PaletteOptions['primary']
  }
}

declare module '@mui/material/Card' {
  interface CardPropsVariantOverrides {
    performance: true
    feeling: true
    security: true
    installation: true
    correlation: true
    office_hours: true
    folderStatus: true
  }
}

declare module '@mui/material/CardHeader' {
  interface CardHeaderPropsVariantOverrides {
    dialog: true
    performance: true
    feeling: true
    security: true
    correlation: true
    installation_overview: true
    installation: true
    karma: true
  }
}

declare module '@mui/material/DialogActions' {
  interface DialogActionsPropsVariantOverrides {
    preloader: true
  }
}

declare module '@mui/material/DialogContent' {
  interface DialogContentPropsVariantOverrides {
    profilePicture: true
    inline: true
  }
}

declare module '@mui/material/DialogTitle' {
  interface DialogTitlePropsVariantOverrides {
    preloader: true
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsVariantOverrides {
    close: true
  }
}

const defaultBorder = '1px solid #DDD'

export const theme = createTheme({
  spacing: 10,

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },

  components: {
    MuiAccordion: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: defaultBorder,
          '&:before': { backgroundColor: '#707070' },
          '&:after': { backgroundColor: '#707070' },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          border: '1px solid',
        },
        standardSuccess: ({ theme }) => ({
          borderColor: theme.palette.success.main,
        }),
        standardWarning: ({ theme }) => ({
          borderColor: theme.palette.warning.main,
        }),
        standardError: ({ theme }) => ({
          borderColor: theme.palette.error.main,
        }),
        standardInfo: ({ theme }) => ({
          borderColor: theme.palette.info.main,
        }),
      },
    },

    MuiButton: {
      defaultProps: { color: 'primary', variant: 'contained' },
      styleOverrides: { root: { borderRadius: 0 } },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      variants: [
        {
          props: { variant: 'performance' },
          style: ({ theme }) => ({
            backgroundColor: '#EEEEEE',
            border: defaultBorder,
            marginBottom: theme.spacing(5),
          }),
        },
        {
          props: { variant: 'feeling' },
          style: {
            backgroundColor: '#EEEEEE',
            border: defaultBorder,
            minHeight: '100%',
          },
        },
        {
          props: { variant: 'security' },
          style: {
            backgroundColor: '#EEEEEE',
            border: defaultBorder,
            minHeight: '100%',
          },
        },
        {
          props: { variant: 'installation' },
          style: ({ theme }) => ({
            backgroundColor: '#EEEEEE',
            border: defaultBorder,
            minHeight: '100%',
            marginBottom: theme.spacing(5),
          }),
        },
        {
          props: { variant: 'correlation' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.white,
            border: defaultBorder,
            minHeight: '100%',
            marginBottom: theme.spacing(2),
          }),
        },
        {
          props: { variant: 'office_hours' },
          style: ({ theme }) => ({
            backgroundColor: '#D3D3D3',
            border: defaultBorder,
            minHeight: '100%',
            marginBottom: theme.spacing(2),
          }),
        },
        {
          props: { variant: 'folderStatus' },
          style: {
            border: defaultBorder,
            borderRadius: '25px',
            '& .MuiCardContent-root': {
              overflow: 'hidden',
              padding: 0,
              position: 'relative',
            },
            '& .MuiCardActions-root': {
              justifyContent: 'space-between',
            },
          },
        },
      ],
    },

    MuiCardHeader: {
      variants: [
        {
          props: { variant: 'dialog' } as any,
          style: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.white,
          }),
        },
        {
          props: { variant: 'performance' } as any,
          style: ({ theme }) => ({
            backgroundColor: theme.palette.white,
            color: theme.palette.black,
            borderBottom: defaultBorder,
          }),
        },
        {
          props: { variant: 'feeling' } as any,
          style: ({ theme }) => ({
            backgroundColor: '#D3D3D3',
            color: theme.palette.black,
            borderBottom: defaultBorder,
          }),
        },
        {
          props: { variant: 'security' } as any,
          style: ({ theme }) => ({
            backgroundColor: '#D3D3D3',
            color: theme.palette.black,
            borderBottom: defaultBorder,
          }),
        },
        {
          props: { variant: 'correlation' } as any,
          style: ({ theme }) => ({
            backgroundColor: theme.palette.white,
            color: theme.palette.black,
            borderBottom: defaultBorder,
          }),
        },
        {
          props: { variant: 'installation_overview' } as any,
          style: ({ theme }) => ({
            backgroundColor: '#D3D3D3',
            color: theme.palette.black,
            borderBottom: defaultBorder,
          }),
        },
        {
          props: { variant: 'installation' } as any,
          style: ({ theme }) => ({
            backgroundColor: theme.palette.white,
            color: theme.palette.black,
            borderBottom: defaultBorder,
          }),
        },
        {
          props: { variant: 'karma' } as any,
          style: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }),
        },
      ],
    },

    MuiCheckbox: {
      defaultProps: { color: 'primary' },
    },

    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
        },
        body: {
          backgroundColor: '#EEEEEE',
          color: '#3C4858',
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          fontSize: '14px',
          fontWeight: 300,
          margin: 0,
          overflow: 'auto',
        },
        a: {
          color: '#9c27b0',
          textDecoration: 'none',
        },
        img: {
          border: 0,
          verticalAlign: 'middle',
        },
        'input[type="file"]': {
          display: 'none',
        },
        code: {
          backgroundColor: 'lightgray',
          padding: '3px',
          borderRadius: '5px',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.white,
          '& .MuiButton-root': {
            backgroundColor: theme.palette.secondary.main,
            borderRadius: 3,
          },
          '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
          },
        }),
      },
      variants: [
        {
          props: { variant: 'preloader' } as any,
          style: ({ theme }) => ({
            backgroundColor: theme.palette.white,
            color: theme.palette.black,
          }),
        },
      ],
    },

    MuiDialogContent: {
      variants: [
        {
          props: { variant: 'profilePicture' } as any,
          style: ({ theme }) => ({
            backgroundColor: theme.palette.black,
            padding: 0,
          }),
        },
        {
          props: { variant: 'inline' } as any,
          style: { margin: 1, padding: 1 },
        },
      ],
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.white,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          '& .MuiTypography-root': {
            fontSize: 20,
          },
        }),
      },
      variants: [
        {
          props: { variant: 'preloader' } as any,
          style: ({ theme }) => ({
            backgroundColor: theme.palette.white,
            color: theme.palette.black,
          }),
        },
      ],
    },

    MuiDrawer: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiDrawer-paper': {
            background: `linear-gradient(45deg, #0E28BB 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.main} 100%)`,
            position: 'fixed',
            height: '100vh',
            width: 260,
          },
        }),
      },
    },

    MuiIconButton: {
      variants: [
        {
          props: { variant: 'close' } as any,
          style: ({ theme }) => ({
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.white,
            '&:hover': {
              backgroundColor: theme.palette.error.main,
            },
          }),
        },
      ],
    },

    MuiPaper: {
      defaultProps: { elevation: 5 },
    },

    MuiRadio: {
      defaultProps: { color: 'primary' },
    },

    MuiSelect: {
      defaultProps: { color: 'primary', variant: 'outlined' },
      styleOverrides: { root: { margin: 10 } },
    },

    MuiSwitch: {
      defaultProps: { color: 'primary' },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          border: '1px solid #DDD',
          borderRadius: '8px',
        },
      },
    },

    MuiTextField: {
      defaultProps: { color: 'primary', variant: 'outlined' },
      styleOverrides: { root: { margin: 10 } },
    },
  },

  palette: {
    mode: 'light',

    primary: {
      main: '#6B00BD',
      light: lighten('#6B00BD', 0.2),
      dark: darken('#6B00BD', 0.2),
    },

    secondary: {
      main: '#DA16F9',
      light: lighten('#DA16F9', 0.2),
      dark: darken('#DA16F9', 0.2),
    },

    success: {
      main: '#9ECF67',
      light: lighten('#9ECF67', 0.2),
      dark: darken('#9ECF67', 0.2),
    },

    warning: {
      main: '#F8E71C',
      light: lighten('#F8E71C', 0.2),
      dark: darken('#F8E71C', 0.2),
    },

    error: {
      main: '#D0021B',
      light: lighten('#D0021B', 0.2),
      dark: darken('#D0021B', 0.2),
    },

    info: {
      main: '#8F8F8F',
      light: lighten('#8F8F8F', 0.2),
      dark: darken('#8F8F8F', 0.2),
    },

    correlation: {
      main: '#00AEF6',
      light: lighten('#00AEF6', 0.2),
      dark: darken('#00AEF6', 0.2),
    },

    lightgray: {
      main: '#DDDDDE',
      light: '#EEEEEE',
      dark: '#CBCBCC',
    },

    black: '#000',
    white: '#FFF',
  },

  typography: {
    button: { textTransform: 'uppercase' },
  },
})
